import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ChevronDownIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export type ComparisonType = 'none' | 'previous_period' | 'previous_week' | 'previous_month' | 'previous_year' | 'custom';
interface ComparisonSelectorProps {
  value: ComparisonType;
  onChange: (comparison: ComparisonType) => void;
  customDateRange?: {
    start: Date;
    end: Date;
  };
  onCustomDateChange?: (range: {
    start: Date;
    end: Date;
  }) => void;
}
export function ComparisonSelector({
  value,
  onChange,
  customDateRange,
  onCustomDateChange
}: ComparisonSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [tempStart, setTempStart] = useState(customDateRange?.start || new Date());
  const [tempEnd, setTempEnd] = useState(customDateRange?.end || new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const options: {
    value: ComparisonType;
    label: string;
  }[] = [{
    value: 'previous_period',
    label: 'Previous period'
  }, {
    value: 'previous_week',
    label: 'Previous week'
  }, {
    value: 'previous_month',
    label: 'Previous month'
  }, {
    value: 'previous_year',
    label: 'Previous year'
  }, {
    value: 'custom',
    label: 'Custom'
  }];
  const selectedOption = options.find(opt => opt.value === value);
  const handleClear = () => {
    onChange('none');
    setIsOpen(false);
    setShowCustomPicker(false);
  };
  const handleOptionClick = (optionValue: ComparisonType) => {
    if (optionValue === 'custom') {
      setShowCustomPicker(true);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };
  const handleApplyCustom = () => {
    onChange('custom');
    if (onCustomDateChange) {
      onCustomDateChange({
        start: tempStart,
        end: tempEnd
      });
    }
    setIsOpen(false);
    setShowCustomPicker(false);
  };
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const formatDateInput = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month} / ${day} / ${year}`;
  };
  const renderCalendar = (monthOffset: number) => {
    const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(displayMonth);
    const firstDay = getFirstDayOfMonth(displayMonth);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" data-id="element-1731" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
      const isInRange = date >= tempStart && date <= tempEnd;
      const isStart = date.toDateString() === tempStart.toDateString();
      const isEnd = date.toDateString() === tempEnd.toDateString();
      days.push(<button key={day} onClick={() => {
        if (!tempStart || tempStart && tempEnd) {
          setTempStart(date);
          setTempEnd(date);
        } else if (date < tempStart) {
          setTempEnd(tempStart);
          setTempStart(date);
        } else {
          setTempEnd(date);
        }
      }} className={`h-8 w-8 rounded-full text-sm flex items-center justify-center ${isStart || isEnd ? 'bg-blue-600 text-white font-medium' : isInRange ? 'bg-blue-100 text-blue-900' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1732">
          {day}
        </button>);
    }
    return <div className="flex-1" data-id="element-1733">
        <div className="flex items-center justify-between mb-2" data-id="element-1734">
          {monthOffset === 0 && <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-1 hover:bg-gray-100 rounded" data-id="element-1735">
              <ChevronLeftIcon size={16} data-id="element-1736" />
            </button>}
          <div className="text-sm font-medium" data-id="element-1737">
            {displayMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
          </div>
          {monthOffset === 1 && <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-1 hover:bg-gray-100 rounded" data-id="element-1738">
              <ChevronRightIcon size={16} data-id="element-1739" />
            </button>}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-1740">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="h-8 flex items-center justify-center text-xs text-gray-500 font-medium" data-id="element-1741">
              {day}
            </div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-id="element-1742">{days}</div>
      </div>;
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1743">
      {value === 'none' ? <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1744">
          <XIcon size={16} data-id="element-1745" />
          <span data-id="element-1746">Compare</span>
          <ChevronDownIcon size={16} data-id="element-1747" />
        </button> : <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100" data-id="element-1748">
          <CheckIcon size={16} data-id="element-1749" />
          <span data-id="element-1750">Compare: {selectedOption?.label}</span>
          <ChevronDownIcon size={16} data-id="element-1751" />
        </button>}

      {isOpen && !showCustomPicker && <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-id="element-1752">
          {options.map(option => <button key={option.value} onClick={() => handleOptionClick(option.value)} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${value === option.value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'}`} data-id="element-1753">
              <span data-id="element-1754">{option.label}</span>
              {value === option.value && <CheckIcon size={16} data-id="element-1755" />}
            </button>)}
          {value !== 'none' && <>
              <div className="border-t border-gray-200 my-1" data-id="element-1756" />
              <button onClick={handleClear} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1757">
                Clear comparison
              </button>
            </>}
        </div>}

      {showCustomPicker && <div className="absolute left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50" style={{
      width: '700px'
    }} data-id="element-1758">
          {/* Date inputs */}
          <div className="flex items-center gap-4 mb-4" data-id="element-1759">
            <div className="flex items-center gap-2" data-id="element-1760">
              <span className="text-sm font-medium text-gray-700" data-id="element-1761">Start</span>
              <input type="text" value={formatDateInput(tempStart)} readOnly className="px-3 py-2 text-sm border border-blue-300 rounded-md bg-blue-50 text-gray-700" data-id="element-1762" />
            </div>
            <div className="flex items-center gap-2" data-id="element-1763">
              <span className="text-sm font-medium text-gray-700" data-id="element-1764">End</span>
              <input type="text" value={formatDateInput(tempEnd)} readOnly className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700" data-id="element-1765" />
            </div>
          </div>

          {/* Calendars */}
          <div className="flex gap-4 mb-4" data-id="element-1766">
            {renderCalendar(0)}
            {renderCalendar(1)}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200" data-id="element-1767">
            <button onClick={() => setShowCustomPicker(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md" data-id="element-1768">
              Clear
            </button>
            <button onClick={handleApplyCustom} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-1769">
              Apply
            </button>
          </div>
        </div>}
    </div>;
}